/* ============================================================
   THEME.JS — Zomer vrouwenkleding store
   Vanilla JS, no dependencies
   ============================================================ */

'use strict';

// ─── Cart state ───────────────────────────────────────────────────────────────

const Cart = (() => {
  const FREE_SHIPPING_THRESHOLD = 5000; // cents (€50)

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function addItem(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }

  async function updateItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  }

  async function removeItem(key) {
    return updateItem(key, 0);
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  function calcProgress(totalCents) {
    return Math.min((totalCents / FREE_SHIPPING_THRESHOLD) * 100, 100);
  }

  function calcRemaining(totalCents) {
    return Math.max(FREE_SHIPPING_THRESHOLD - totalCents, 0);
  }

  return { getCart, addItem, updateItem, removeItem, formatMoney, calcProgress, calcRemaining, FREE_SHIPPING_THRESHOLD };
})();


// ─── Cart Drawer ──────────────────────────────────────────────────────────────

const CartDrawer = (() => {
  const drawer  = document.querySelector('.cart-drawer');
  const overlay = document.querySelector('.cart-overlay');
  const body    = document.querySelector('.cart-drawer__body');
  const countEls = document.querySelectorAll('.cart-count');

  if (!drawer) return { open: () => {}, refresh: () => {} };

  function open() {
    drawer.classList.add('open');
    overlay.classList.add('open');
    document.body.classList.add('no-scroll');
    refresh();
  }

  function close() {
    drawer.classList.remove('open');
    overlay.classList.remove('open');
    document.body.classList.remove('no-scroll');
  }

  async function refresh() {
    const cart = await Cart.getCart();
    renderItems(cart);
    updateProgress(cart);
    updateCount(cart.item_count);
    // also update sticky ATC price if on product page
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  function updateCount(count) {
    countEls.forEach(el => {
      el.textContent = count;
      el.classList.toggle('visible', count > 0);
    });
  }

  function updateProgress(cart) {
    const progress = document.querySelector('.cart-progress__fill');
    const label    = document.querySelector('.cart-progress__label');
    if (!progress) return;

    const pct       = Cart.calcProgress(cart.total_price);
    const remaining = Cart.calcRemaining(cart.total_price);

    progress.style.width = `${pct}%`;

    if (remaining === 0) {
      label.textContent = '🎉 Je hebt gratis verzending verdiend!';
      label.style.color = 'var(--color-sage)';
    } else {
      label.textContent = `Nog ${Cart.formatMoney(remaining)} voor gratis verzending`;
      label.style.color = '';
    }
  }

  function renderItems(cart) {
    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty" style="text-align:center;padding:3rem 1rem">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-border)" stroke-width="1.2" style="margin:0 auto 1rem"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
          <p style="font-size:var(--font-size-md);margin-bottom:.5rem;color:var(--color-charcoal)">Je winkelwagen is leeg</p>
          <p style="font-size:var(--font-size-sm);margin-bottom:1.5rem">Ontdek onze zomercollectie</p>
          <a href="/collections/all" class="btn btn--primary btn--sm">Bekijk collectie</a>
        </div>`;
      return;
    }

    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__image" src="${item.image}" alt="${item.title}" loading="lazy" width="80" height="100">
        <div class="cart-item__info">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          ${item.variant_title ? `<span class="cart-item__variant">${item.variant_title}</span>` : ''}
          <div class="cart-item__footer">
            <div class="qty-control" aria-label="Aantal aanpassen">
              <button class="qty-decrease" data-key="${item.key}" aria-label="Minder">−</button>
              <input class="qty-control__value" type="number" value="${item.quantity}" min="0" max="99"
                     data-key="${item.key}" aria-label="Aantal">
              <button class="qty-increase" data-key="${item.key}" aria-label="Meer">+</button>
            </div>
            <span class="cart-item__price">${Cart.formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item__remove" data-key="${item.key}">Verwijderen</button>
        </div>
      </div>
    `).join('');

    // update subtotal
    const subtotal = document.querySelector('.cart-subtotal__value');
    if (subtotal) subtotal.textContent = Cart.formatMoney(cart.total_price);

    bindItemEvents();
  }

  function bindItemEvents() {
    body.querySelectorAll('.qty-decrease').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const input = body.querySelector(`input[data-key="${key}"]`);
        const newQty = Math.max(0, parseInt(input.value) - 1);
        input.value = newQty;
        await Cart.updateItem(key, newQty);
        refresh();
      });
    });

    body.querySelectorAll('.qty-increase').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const input = body.querySelector(`input[data-key="${key}"]`);
        const newQty = parseInt(input.value) + 1;
        input.value = newQty;
        await Cart.updateItem(key, newQty);
        refresh();
      });
    });

    body.querySelectorAll('.qty-control__value').forEach(input => {
      input.addEventListener('change', async () => {
        const newQty = Math.max(0, parseInt(input.value) || 0);
        input.value = newQty;
        await Cart.updateItem(input.dataset.key, newQty);
        refresh();
      });
    });

    body.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        const item = btn.closest('.cart-item');
        item.style.opacity = '.4';
        item.style.pointerEvents = 'none';
        await Cart.removeItem(btn.dataset.key);
        refresh();
      });
    });
  }

  // bind open triggers
  document.querySelectorAll('[data-cart-open]').forEach(el => {
    el.addEventListener('click', open);
  });

  overlay.addEventListener('click', close);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });

  return { open, close, refresh, updateCount };
})();


// ─── Add to Cart ──────────────────────────────────────────────────────────────

function initAddToCart() {
  document.querySelectorAll('[data-atc-form]').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();

      const btn = form.querySelector('[data-atc-btn]');
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) return;

      const originalText = btn.innerHTML;
      btn.classList.add('loading');
      btn.innerHTML = `<span class="spinner"></span> Toevoegen...`;

      try {
        await Cart.addItem(variantInput.value);
        btn.innerHTML = '✓ Toegevoegd';
        btn.style.background = 'var(--color-sage)';
        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.style.background = '';
          btn.classList.remove('loading');
        }, 1800);
        CartDrawer.open();
      } catch {
        btn.innerHTML = 'Probeer opnieuw';
        btn.classList.remove('loading');
        setTimeout(() => {
          btn.innerHTML = originalText;
        }, 2000);
      }
    });
  });
}


// ─── Sticky ATC ───────────────────────────────────────────────────────────────

function initStickyATC() {
  const stickyBar = document.querySelector('.sticky-atc');
  if (!stickyBar) return;

  const atcBtn = document.querySelector('[data-atc-btn]');
  if (!atcBtn) return;

  const observer = new IntersectionObserver(entries => {
    // show sticky when the main ATC button is out of view
    stickyBar.classList.toggle('visible', !entries[0].isIntersecting);
  }, { rootMargin: '-80px 0px 0px 0px' });

  observer.observe(atcBtn);

  // sticky ATC submits the same form
  const stickyBtn = stickyBar.querySelector('[data-atc-btn]');
  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => {
      const mainForm = document.querySelector('[data-atc-form]');
      if (mainForm) mainForm.requestSubmit();
    });
  }
}


// ─── Variant selector ─────────────────────────────────────────────────────────

function initVariantSelector() {
  const form = document.querySelector('[data-atc-form]');
  if (!form) return;

  const variantData = window.__variantData__;
  if (!variantData) return;

  function getSelectedOptions() {
    const opts = [];
    form.querySelectorAll('[data-option-position]').forEach(input => {
      if (input.type === 'radio' && input.checked) opts.push(input.value);
      if (input.type === 'hidden') opts.push(input.value);
    });
    // from button groups
    form.querySelectorAll('.size-btns .size-btn.active, .color-swatches .color-swatch-btn.active').forEach(btn => {
      const pos = btn.closest('[data-option-group]')?.dataset.optionPosition;
      if (pos) opts[parseInt(pos) - 1] = btn.dataset.value;
    });
    return opts;
  }

  function findVariant(opts) {
    return variantData.find(v =>
      v.options.every((opt, i) => opt === opts[i])
    );
  }

  function updateUI(variant) {
    const priceEl   = document.querySelector('.price--current');
    const compareEl = document.querySelector('.price--compare');
    const savingsEl = document.querySelector('.price--savings');
    const atcBtn    = document.querySelector('[data-atc-btn]');
    const idInput   = form.querySelector('[name="id"]');
    const galleryImg = document.querySelector('.product-gallery__main img');
    const scarcityEl = document.querySelector('.product-scarcity');

    if (!variant) {
      if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = 'Niet beschikbaar'; }
      return;
    }

    if (idInput) idInput.value = variant.id;

    if (priceEl) priceEl.textContent = Cart.formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = Cart.formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
        if (savingsEl) {
          const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
          savingsEl.textContent = `−${pct}%`;
          savingsEl.style.display = '';
        }
      } else {
        compareEl.style.display = 'none';
        if (savingsEl) savingsEl.style.display = 'none';
      }
    }

    if (atcBtn) {
      if (variant.available) {
        atcBtn.disabled = false;
        atcBtn.textContent = 'In winkelwagen';
      } else {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Uitverkocht';
      }
    }

    // update scarcity
    if (scarcityEl && variant.inventory_management === 'shopify') {
      const qty = variant.inventory_quantity;
      if (qty > 0 && qty <= 5) {
        scarcityEl.style.display = 'flex';
        scarcityEl.querySelector('[data-scarcity-count]').textContent = qty;
      } else {
        scarcityEl.style.display = 'none';
      }
    }

    // swap gallery image if variant has a featured image
    if (variant.featured_image && galleryImg) {
      galleryImg.src = variant.featured_image.src.replace(/(\.[a-z]+)$/, '_800x$1');
      galleryImg.srcset = '';
    }

    // update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  function bindButtons(group) {
    const btns = group.querySelectorAll('.size-btn, .color-swatch-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const opts = getSelectedOptions();
        updateUI(findVariant(opts));
      });
    });
  }

  form.querySelectorAll('[data-option-group]').forEach(bindButtons);
}


// ─── Product gallery ──────────────────────────────────────────────────────────

function initProductGallery() {
  const main   = document.querySelector('.product-gallery__main');
  const thumbs = document.querySelectorAll('.product-gallery__thumb');
  const dots   = document.querySelector('.product-gallery__dots');

  if (!main || !thumbs.length) return;

  let current = 0;

  const images = Array.from(thumbs).map(t => ({
    src:    t.dataset.src,
    srcset: t.dataset.srcset || '',
    alt:    t.dataset.alt || '',
  }));

  function goTo(idx) {
    current = idx;
    const mainImg = main.querySelector('img');
    if (mainImg && images[idx]) {
      mainImg.src    = images[idx].src;
      mainImg.srcset = images[idx].srcset;
      mainImg.alt    = images[idx].alt;
    }

    thumbs.forEach((t, i) => t.classList.toggle('active', i === idx));

    if (dots) {
      dots.querySelectorAll('.gallery-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
    }
  }

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => goTo(i));
  });

  // mobile swipe
  let touchStartX = 0;
  let touchStartY = 0;

  main.addEventListener('touchstart', e => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  main.addEventListener('touchend', e => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    const dy = e.changedTouches[0].clientY - touchStartY;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      const next = dx < 0
        ? Math.min(current + 1, images.length - 1)
        : Math.max(current - 1, 0);
      goTo(next);
    }
  });

  // build dots for mobile
  if (dots && images.length > 1) {
    dots.innerHTML = images.map((_, i) =>
      `<button class="gallery-dot${i === 0 ? ' active' : ''}" aria-label="Foto ${i+1}"></button>`
    ).join('');
    dots.querySelectorAll('.gallery-dot').forEach((dot, i) => {
      dot.addEventListener('click', () => goTo(i));
    });
  }
}


// ─── Wishlist ─────────────────────────────────────────────────────────────────

const Wishlist = (() => {
  const KEY = 'wishlist_items';

  function getItems() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  }

  function toggle(id) {
    let items = getItems();
    if (items.includes(id)) {
      items = items.filter(i => i !== id);
    } else {
      items.push(id);
    }
    localStorage.setItem(KEY, JSON.stringify(items));
    return items.includes(id);
  }

  function has(id) { return getItems().includes(id); }

  return { toggle, has, getItems };
})();

function initWishlist() {
  document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
    const id = btn.dataset.productId;
    if (Wishlist.has(id)) btn.classList.add('active');

    btn.addEventListener('click', e => {
      e.preventDefault();
      const active = Wishlist.toggle(id);
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active);

      // mini toast
      showToast(active ? 'Toegevoegd aan verlanglijst ♡' : 'Verwijderd van verlanglijst');
    });
  });
}


// ─── Toast ────────────────────────────────────────────────────────────────────

let toastTimeout;

function showToast(message, duration = 2800) {
  let toast = document.querySelector('.toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    toast.style.cssText = `
      position:fixed;bottom:var(--space-6);left:50%;transform:translateX(-50%) translateY(20px);
      background:var(--color-charcoal);color:#fff;padding:var(--space-3) var(--space-5);
      border-radius:var(--radius-full);font-size:var(--font-size-sm);z-index:500;
      opacity:0;transition:all .25s;pointer-events:none;white-space:nowrap;
    `;
    document.body.appendChild(toast);
  }

  clearTimeout(toastTimeout);
  toast.textContent = message;
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
  });

  toastTimeout = setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(20px)';
  }, duration);
}


// ─── Sticky header ────────────────────────────────────────────────────────────

function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  let lastY = 0;

  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    header.classList.toggle('scrolled', y > 10);
    lastY = y;
  }, { passive: true });
}


// ─── Accordion ────────────────────────────────────────────────────────────────

function initAccordion() {
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      const body = item.querySelector('.accordion-body');
      const open = item.classList.contains('open');

      // close all in same accordion
      const accordion = item.closest('.accordion');
      accordion.querySelectorAll('.accordion-item.open').forEach(i => {
        i.classList.remove('open');
        i.querySelector('.accordion-body').style.maxHeight = '0';
      });

      if (!open) {
        item.classList.add('open');
        body.style.maxHeight = body.scrollHeight + 'px';
      }

      trigger.setAttribute('aria-expanded', !open);
    });
  });
}


// ─── Countdown timer ──────────────────────────────────────────────────────────

function initCountdown() {
  document.querySelectorAll('[data-countdown]').forEach(el => {
    const endTime = new Date(el.dataset.countdown).getTime();
    if (isNaN(endTime)) return;

    const daysEl    = el.querySelector('[data-cd-days]');
    const hoursEl   = el.querySelector('[data-cd-hours]');
    const minutesEl = el.querySelector('[data-cd-minutes]');
    const secondsEl = el.querySelector('[data-cd-seconds]');

    function tick() {
      const now  = Date.now();
      const diff = Math.max(endTime - now, 0);

      if (diff === 0) {
        el.closest('[data-countdown-wrap]')?.remove();
        return;
      }

      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000)  / 60000);
      const s = Math.floor((diff % 60000)    / 1000);

      if (daysEl)    daysEl.textContent    = String(d).padStart(2, '0');
      if (hoursEl)   hoursEl.textContent   = String(h).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(m).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(s).padStart(2, '0');

      setTimeout(tick, 1000);
    }

    tick();
  });
}


// ─── Social proof counter ─────────────────────────────────────────────────────

function initSocialProof() {
  const el = document.querySelector('[data-live-viewers]');
  if (!el) return;

  // Simulate realistic viewer count (replace with real data in production)
  const base = parseInt(el.dataset.base) || 8;

  function update() {
    const jitter = Math.floor(Math.random() * 5) - 2; // ±2
    const count  = Math.max(2, base + jitter);
    el.textContent = count;
  }

  update();
  setInterval(update, 12000); // update every 12 seconds
}


// ─── Newsletter popup ─────────────────────────────────────────────────────────

function initNewsletterPopup() {
  const overlay = document.querySelector('.popup-overlay');
  if (!overlay) return;

  const dismissed = sessionStorage.getItem('popup_dismissed');
  if (dismissed) return;

  const delay = parseInt(overlay.dataset.delay) || 5000;
  let shown = false;

  function show() {
    if (shown) return;
    shown = true;
    overlay.classList.add('open');
    document.body.classList.add('no-scroll');
  }

  function hide() {
    overlay.classList.remove('open');
    document.body.classList.remove('no-scroll');
    sessionStorage.setItem('popup_dismissed', '1');
  }

  // Timer trigger
  setTimeout(show, delay);

  // Exit intent (desktop only)
  if (window.innerWidth > 768) {
    document.addEventListener('mouseleave', e => {
      if (e.clientY < 10) show();
    });
  }

  // Close handlers
  overlay.querySelector('.popup__close')?.addEventListener('click', hide);
  overlay.querySelector('.popup__skip')?.addEventListener('click', hide);
  overlay.addEventListener('click', e => {
    if (e.target === overlay) hide();
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && overlay.classList.contains('open')) hide();
  });

  // Form submit
  const form = overlay.querySelector('form');
  form?.addEventListener('submit', async e => {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    btn.disabled = true;
    btn.textContent = 'Bezig...';

    // Klaviyo / customer endpoint — adapt as needed
    try {
      const email = form.querySelector('[name="email"]').value;
      await fetch('/contact#contact_form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `form_type=customer&utf8=✓&contact[email]=${encodeURIComponent(email)}&contact[tags]=newsletter`,
      });
    } catch { /* fail silently */ }

    form.innerHTML = `
      <div style="text-align:center;padding:1rem 0">
        <div style="font-size:2rem;margin-bottom:.5rem">🌿</div>
        <p style="font-size:var(--font-size-md);font-weight:500;color:var(--color-charcoal);margin-bottom:.25rem">Gelukt!</p>
        <p style="font-size:var(--font-size-sm)">Je kortingscode <strong>WELKOM10</strong> is onderweg.</p>
      </div>`;

    setTimeout(hide, 3000);
  });
}


// ─── Announcement bar cycling ─────────────────────────────────────────────────

function initAnnouncementBar() {
  const bar = document.querySelector('.announcement-bar--cycling');
  if (!bar) return;

  const items = bar.querySelectorAll('.announcement-bar__item');
  if (items.length <= 1) return;

  let idx = 0;
  items[0].style.opacity = '1';

  setInterval(() => {
    items[idx].style.opacity = '0';
    idx = (idx + 1) % items.length;
    items[idx].style.opacity = '1';
  }, 4000);

  items.forEach(item => {
    item.style.transition = 'opacity .4s';
    item.style.position = 'absolute';
    item.style.opacity = '0';
  });

  bar.style.position = 'relative';
}


// ─── Delivery countdown (cut-off timer) ──────────────────────────────────────

function initDeliveryTimer() {
  const el = document.querySelector('[data-delivery-cutoff]');
  if (!el) return;

  const cutoffHour = parseInt(el.dataset.deliveryCutoff) || 14; // 14:00

  function tick() {
    const now  = new Date();
    const cutoff = new Date();
    cutoff.setHours(cutoffHour, 0, 0, 0);

    if (now > cutoff) {
      cutoff.setDate(cutoff.getDate() + 1); // tomorrow
    }

    const diff = cutoff - now;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);

    el.textContent = `Bestel binnen ${h}u ${m}m ${s}s voor morgen in huis`;
    setTimeout(tick, 1000);
  }

  tick();
}


// ─── Recently viewed ──────────────────────────────────────────────────────────

const RecentlyViewed = (() => {
  const KEY = 'rv_products';
  const MAX  = 8;

  function getItems() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  }

  function track(productId, title, url, imgSrc, price) {
    let items = getItems().filter(i => i.id !== productId);
    items.unshift({ id: productId, title, url, imgSrc, price });
    if (items.length > MAX) items = items.slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(items));
  }

  function render(container, excludeId) {
    const items = getItems().filter(i => i.id !== excludeId);
    if (!items.length) { container.closest('[data-rv-section]')?.remove(); return; }

    container.innerHTML = items.map(item => `
      <a href="${item.url}" class="product-card">
        <div class="product-card__media">
          <img src="${item.imgSrc}" alt="${item.title}" loading="lazy" width="300" height="400">
        </div>
        <div class="product-card__info">
          <p class="product-card__title">${item.title}</p>
          <div class="product-card__price">
            <span class="product-card__price-current">${item.price}</span>
          </div>
        </div>
      </a>
    `).join('');
  }

  return { track, render };
})();

function initRecentlyViewed() {
  // Track on product pages
  const trackEl = document.querySelector('[data-rv-track]');
  if (trackEl) {
    RecentlyViewed.track(
      trackEl.dataset.productId,
      trackEl.dataset.title,
      trackEl.dataset.url,
      trackEl.dataset.img,
      trackEl.dataset.price,
    );
  }

  // Render the section
  const renderEl = document.querySelector('[data-rv-grid]');
  if (renderEl) {
    const excludeId = document.querySelector('[data-rv-track]')?.dataset.productId;
    RecentlyViewed.render(renderEl, excludeId);
  }
}


// ─── Quick view modal ─────────────────────────────────────────────────────────

function initQuickView() {
  document.querySelectorAll('[data-quick-view]').forEach(btn => {
    btn.addEventListener('click', async e => {
      e.preventDefault();
      const handle = btn.dataset.quickView;
      try {
        const res  = await fetch(`/products/${handle}?view=quick-view`);
        const html = await res.text();
        const modal = document.querySelector('#quick-view-modal') || createModal();
        modal.querySelector('.modal__body').innerHTML = html;
        modal.classList.add('open');
        document.body.classList.add('no-scroll');
        initVariantSelector();
        initProductGallery();
      } catch {}
    });
  });

  function createModal() {
    const modal = document.createElement('div');
    modal.id = 'quick-view-modal';
    modal.innerHTML = `
      <div class="modal-overlay"></div>
      <div class="modal" style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);
           width:min(680px,95vw);max-height:90vh;overflow-y:auto;background:var(--color-creme);
           border-radius:var(--radius-xl);z-index:300;padding:var(--space-6)">
        <button class="modal__close" style="position:absolute;top:1rem;right:1rem;width:32px;height:32px;
          background:var(--color-linen);border-radius:50%;display:flex;align-items:center;justify-content:center">×</button>
        <div class="modal__body"></div>
      </div>`;
    modal.querySelector('.modal__close').addEventListener('click', () => {
      modal.classList.remove('open');
      document.body.classList.remove('no-scroll');
    });
    document.body.appendChild(modal);
    return modal;
  }
}


// ─── Mobile menu ──────────────────────────────────────────────────────────────

function initMobileMenu() {
  const toggle  = document.querySelector('[data-mobile-menu-toggle]');
  const menu    = document.querySelector('.mobile-menu');
  const overlay = document.querySelector('.mobile-menu-overlay');
  if (!toggle || !menu) return;

  function open() {
    menu.classList.add('open');
    overlay?.classList.add('open');
    document.body.classList.add('no-scroll');
    toggle.setAttribute('aria-expanded', 'true');
  }

  function close() {
    menu.classList.remove('open');
    overlay?.classList.remove('open');
    document.body.classList.remove('no-scroll');
    toggle.setAttribute('aria-expanded', 'false');
  }

  toggle.addEventListener('click', () => {
    menu.classList.contains('open') ? close() : open();
  });

  overlay?.addEventListener('click', close);
}


// ─── Boot ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initStickyHeader();
  initMobileMenu();
  initAccordion();
  initCountdown();
  initDeliveryTimer();
  initAddToCart();
  initStickyATC();
  initVariantSelector();
  initProductGallery();
  initWishlist();
  initNewsletterPopup();
  initAnnouncementBar();
  initSocialProof();
  initRecentlyViewed();
  initQuickView();
});
